import { useDisclosure } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from 'components/Modal';
import Table from 'components/Table';
import FilterGroup from 'containers/JobsTasks/shared/FilterGroup';
import { useTableColumns } from 'containers/JobsTasks/TasksTabs/Active/useTableColumns';
import { useDeactivateTasks } from 'hooks/task/useDeactivateTasks';
import { useDeleteTasks, IDeleteTasksData } from 'hooks/task/useDeleteTasks';
import { ITasksSearchQuery, useFetchPaginatedTasks } from 'hooks/task/useFetchPaginatedTasks';

import TasksTableFooter from './TasksTableFooter';

const initialTasksSearchQuery: ITasksSearchQuery = {
  page: 1,
  page_size: 25,
  task_status: '1',
  search: '',
  sort: '',
};

const Active: React.FC<unknown> = () => {
  const { t } = useTranslation(['jobsTasks', 'common']);

  const [dialogIds, setDialogIds] = useState<IDeleteTasksData>({ ids: [] });
  const [searchQuery, setSearchQuery] = useState<ITasksSearchQuery>(initialTasksSearchQuery);

  const {
    isOpen: isDeactivateModalOpen,
    onOpen: onDeactivateModalOpen,
    onClose: onDeactivateModalClose,
  } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const { status, data, refetch } = useFetchPaginatedTasks(searchQuery);
  const { status: deactivateTasksStatus, mutateAsync: mutateAsyncDeactivatedTasks } = useDeactivateTasks();
  const { status: deleteTasksStatus, mutateAsync: mutateAsyncDeletedTasks } = useDeleteTasks();

  const handleSortChange = useCallback(
    (selectedSort: ILabelValueOptions) => {
      if (selectedSort) {
        setSearchQuery({ ...searchQuery, page: 1, sort: selectedSort.value });
      } else {
        setSearchQuery({ ...searchQuery, page: 1, sort: '' });
      }
    },
    [searchQuery]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageSizeChange = (pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  };

  // deactivate user handler
  const handleSingleDeactivate = (id: number) => {
    setDialogIds({ ids: [id] });
    onDeactivateModalOpen();
  };

  const handleBulkDeactivate = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeactivateModalOpen();
  };

  const deactivateTasks = () => {
    mutateAsyncDeactivatedTasks(dialogIds).then(() => {
      refetch();
      onDeactivateModalClose();
      setDialogIds({ ids: [] });
    });
  };

  // delete user handler
  const handleSingleDelete = (id: number) => {
    setDialogIds({ ids: [id] });
    onDeleteModalOpen();
  };
  // delete user handler
  const handleBulkDelete = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeleteModalOpen();
  };

  const deleteTasks = () => {
    mutateAsyncDeletedTasks(dialogIds).then(() => {
      refetch();
      onDeleteModalClose();
      setDialogIds({ ids: [] });
    });
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: ITasksSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const { columns } = useTableColumns({ handleSingleDeactivate, handleSingleDelete });

  return (
    <div>
      <ConfirmationModal
        isOpen={isDeactivateModalOpen}
        onClose={onDeactivateModalClose}
        title={t('jobsTasks:deactivateTaskModalTitle')}
        subTitle={t('jobsTasks:deactivateTaskModalSubTitle')}
        confirmButtonLabel={t('common:deactivate')}
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: deactivateTasksStatus === 'loading',
          onClick: deactivateTasks,
        }}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('jobsTasks:deleteTaskModalTitle')}
        subTitle={t('jobsTasks:deleteTaskModalSubTitle')}
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: deleteTasksStatus === 'loading',
          onClick: deleteTasks,
        }}
      />
      <FilterGroup
        handleSearchInputChange={handleSearchInputChange}
        handleSortChange={handleSortChange}
        entity="Task"
      />
      <Table
        columns={columns}
        data={data?.payload || []}
        paginationData={data?.meta_data?.pagination}
        isLoading={status === 'loading'}
        enableRowSelect
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        emptyTableStateProps={{
          searchText: searchQuery.search,
        }}
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <TasksTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkDeactivate={handleBulkDeactivate}
              handleBulkDelete={handleBulkDelete}
            />
          );
        }}
      />
    </div>
  );
};

export default Active;
